import { useState, useCallback, useEffect } from 'react';
import { calculatePF } from '@/utils/calculator';

export interface ParamsCardProps {
  initialTs: number | null;
  onCalculate: (result: {
    ts: number;
    M: number;
    a: number;
    hc: number;
    pf: number;
    isExtrapolated: boolean;
    note: string;
  }) => void;
}

export default function ParamsCard({ initialTs, onCalculate }: ParamsCardProps) {
  const [ts, setTs] = useState(initialTs !== null ? initialTs.toFixed(2) : '');
  const [hc, setHc] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialTs !== null) {
      setTs(initialTs.toFixed(2));
    }
  }, [initialTs]);

  const handleCalculate = useCallback(() => {
    setError('');

    const tsVal = parseFloat(ts);
    const hcVal = parseFloat(hc);

    if (Number.isNaN(tsVal)) {
      setError('请输入土壤温度 Ts');
      return;
    }

    if (Number.isNaN(hcVal)) {
      setError('请输入 H/C_org 值');
      return;
    }

    if (hcVal < 0) {
      setError('H/C_org 不能为负数');
      return;
    }

    const result = calculatePF(tsVal, hcVal);
    onCalculate(result);
  }, [ts, hc, onCalculate]);

  return (
    <div className="bg-background-50 rounded-2xl p-5 md:p-8 border border-background-200/70 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-accent-500/15 flex items-center justify-center">
          <i className="ri-settings-4-line text-accent-600 text-xl" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">参数</h2>
          <p className="text-sm text-foreground-600 mt-0.5">输入方法学所需计算参数</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground-800">
            土壤温度 Ts（°C）
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
              7–40 范围内插值
            </span>
          </label>
          <input
            type="number"
            step="any"
            placeholder="点①自动填入，或手动输入"
            value={ts}
            onChange={(e) => { setTs(e.target.value); setError(''); }}
            className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground-800">
            H/C_org（生物炭氢碳有机摩尔比）
          </label>
          <input
            type="number"
            step="any"
            placeholder="需实测值，例如 0.4"
            value={hc}
            onChange={(e) => { setHc(e.target.value); setError(''); }}
            className="w-full bg-background-100 border border-background-300/60 rounded-xl px-4 py-3 text-sm text-foreground-950 placeholder-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 transition-all"
          />
        </div>
      </div>

      <p className="text-xs text-foreground-500 mb-5">
        H/C_org 必须为该批次生物炭的实测值（依据方法学 6.2.2 节要求），此工具不提供默认值代填
      </p>

      {error && (
        <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <button
        onClick={handleCalculate}
        className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm rounded-xl py-3.5 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
      >
        <i className="ri-calculator-line" />
        计算持久系数 PF
      </button>
    </div>
  );
}